import chance from '@/modules/base/services/chance';
import {Team} from "@/modules/team/entity";
import {inject, injectable} from "inversify";
import {BankCategory} from "@/modules/finances/enums";
import RandomLineupper from "@/modules/ai/services/lineupper/RandomLineupper";
import {EventEmitter} from "@/modules/base/events";
import {BeforeTeamCreatedEvent} from "@/modules/team/events/BeforeTeamCreatedEvent";
import {AfterTeamCreatedEvent} from "@/modules/team/events/AfterTeamCreatedEvent";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {AIQualityLevelEnum} from "@/modules/ai/enums";
import {GateTypeEnum} from "@/modules/action-points/enums";
import GateManager from "@/modules/action-points/services/GateManager";
import {Country} from "@/modules/base/entity";
import {FormationEnum} from "@/modules/team/enums";
import {LineupEnum} from "@/modules/base/enums";
import AIServiceProvider from "@/modules/ai/services/AIServiceProvider";
import FastPlayerFactory from "@/modules/team/services/factories/FastPlayerFactory";
import TeamNameGenerator from "@/modules/team/services/TeamNameGenerator";
import CityRepository from "@/modules/team/services/CityRepository";
import TeamColorGenerator from "@/modules/team/services/TeamColorGenerator";

@injectable()
export default class TeamFactory {
  readonly percentageOfForeigners: number = 50;

  constructor(
    @inject(GateManager) private gateManager: GateManager,
    @inject(EventEmitter) private em: EventEmitter,
    @inject(FastPlayerFactory) private playerFactory: FastPlayerFactory,
    @inject(RandomLineupper) private randomLineupper: RandomLineupper,
    @inject(ConfigRepository) private configRepository: ConfigRepository,
    @inject(AIServiceProvider) private aiServiceProvider: AIServiceProvider,
    @inject(TeamNameGenerator) private teamNameGenerator: TeamNameGenerator,
    @inject(CityRepository) private cityRepository: CityRepository,
    @inject(TeamColorGenerator) private teamColorGenerator: TeamColorGenerator,
  ) {
  }

  async generate(country: Country, level: number, callback?: (team: Team) => void): Promise<Team> {
    console.time('time::TeamFactory::generate');
    const team = new Team();

    await this.em.emitAsync(new BeforeTeamCreatedEvent(team));

    const city = this.cityRepository.getOneByLevel(country.name, level);
    const names = this.teamNameGenerator.generate({country: country.name, city});
    const colors = this.teamColorGenerator.generate();

    team.level = level;
    team.country = country;
    team.name = names.long;
    team.shortName = names.short;
    team.primaryColor = colors.primary;
    team.secondaryColor = colors.secondary;
    team.prestige = chance.integer({min: 50, max: 200});
    team.patience = chance.integer({min: 1, max: 100});
    team.greediness = chance.integer({min: 1, max: 100});
    team.resentfulness = chance.integer({min: 1, max: 100});
    team.aiQualityLevel = chance.randomEnum(AIQualityLevelEnum);
    team.formation = chance.randomEnum(FormationEnum);
    team.actionPointsCoins = this.configRepository.get('coins.startAmount');

    if (callback) {
      callback(team);
    }

    await team.save();

    const definition = this.configRepository.get('formations.all')[team.formation];

    await times(22).mapAsync(async (index: number) => {
      return this.playerFactory.generateYoungFiller(team, {
        callback: p => {
          const lineup: LineupEnum = (index % 11) + 1 as LineupEnum;
          p.position = definition[lineup].position;
          p.age = chance.integer({min: 18, max: 35});
          p.skill *= 2;
        }
      });
    });

    // todo can be removed for fasted game creation?
    await this.playerFactory.flush();

    if (team.user) {
      await this.gateManager.getGate(GateTypeEnum.BALANCE).currency.add(
        this.configRepository.get('general.startBalance'),
        {
          category: BankCategory.REWARDS,
          label: translator.t('initial_reward'),
        }
      );
    }

    await this.em.emitAsync(new AfterTeamCreatedEvent(team));
    console.timeEnd('time::TeamFactory::generate');

    return team;
  }
}
